import {
  Button,
  Card,
  IconButton,
  Menu,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { MoreVertIcon } from "@braintrust/braintrust-ui-components/Icons";
import type {
  PaymentMethod,
  StripeACHPayment,
  StripeCreditCardPayment,
  StripeSEPAPayment,
} from "@js/types/payments";

import { PaymentMethodVendorImage } from "../vendor-image";

import { StripeACHItem } from "./card-type/stripe-ach-bank-account";
import { StripeCCItem } from "./card-type/stripe-credit-debit-card";
import { StripeSEPAItem } from "./card-type/stripe-sepa";

import styles from "./style.module.scss";

export type PaymentMethodListItemProps = {
  paymentMethod: PaymentMethod;
  hideVerifyButton?: boolean;
  onDelete: (paymentMethod: PaymentMethod) => void;
  onVerifyStripeACHPaymentMethod?: (paymentMethod: StripeACHPayment) => void;
  onSetAsDefault?: (paymentMethod: PaymentMethod) => void;
  isDeleteOptionDisabled: boolean;
};

export const PaymentMethodListItem = (props: PaymentMethodListItemProps) => {
  const { paymentMethod } = props;

  switch (paymentMethod.content_type) {
    case ENUMS.PaymentContentType.StripeACHPaymentMethod:
      return (
        <PaymentMethodStripeACHListItem
          {...props}
          paymentMethod={paymentMethod}
        />
      );

    case ENUMS.PaymentContentType.StripeCreditCardPaymentMethod:
      return (
        <PaymentMethodStripeCCListItem
          {...props}
          paymentMethod={paymentMethod}
        />
      );

    case ENUMS.PaymentContentType.StripeSepaDirectDebitPaymentMethod:
      return (
        <PaymentMethodStripeSEPAListItem
          {...props}
          paymentMethod={paymentMethod}
        />
      );

    default:
      paymentMethod satisfies never;
      return null;
  }
};

const PaymentMethodDetaultTag = () => {
  return (
    <Button
      disabled
      size="x-small"
      variant="primary"
      color="secondary"
      className={styles.paymentMethodsListItemDefaultButton}
    >
      DEFAULT
    </Button>
  );
};

const PaymentMethodItemMenu = ({
  isDeleteOptionDisabled,
  paymentMethod,
  onDelete,
  onSetAsDefault,
}: Pick<
  PaymentMethodListItemProps,
  "isDeleteOptionDisabled" | "onDelete" | "paymentMethod" | "onSetAsDefault"
>) => {
  return (
    <Menu
      id={`payment-method-${paymentMethod.id}-details-menu`}
      anchor={
        <IconButton
          aria-controls={`payment-method-${paymentMethod.id}-details-menu`}
          aria-haspopup="true"
          aria-label="more menu"
          variant="transparent"
        >
          <MoreVertIcon />
        </IconButton>
      }
      anchorOrigin={{
        horizontal: "right",
        vertical: "bottom",
      }}
      transformOrigin={{
        horizontal: "right",
        vertical: "top",
      }}
    >
      <Menu.Item
        key="delete"
        disabled={isDeleteOptionDisabled}
        tooltipText="Cannot remove payment method used in the deposit. Please change the deposit payment method and then remove this one."
        onClick={() => {
          if (onDelete) {
            onDelete(paymentMethod);
          }
        }}
      >
        Delete
      </Menu.Item>
      {paymentMethod.can_be_set_as_default && !paymentMethod.is_default && (
        <Menu.Item
          key="default"
          onClick={() => {
            if (onSetAsDefault) {
              onSetAsDefault(paymentMethod);
            }
          }}
        >
          Make Default
        </Menu.Item>
      )}
    </Menu>
  );
};

type PaymentMethodListItemBaseProps<TMethod extends PaymentMethod> = Omit<
  PaymentMethodListItemProps,
  "paymentMethod"
> & {
  paymentMethod: TMethod;
};

export const PaymentMethodStripeACHListItem = ({
  paymentMethod,
  hideVerifyButton = false,
  onDelete,
  onSetAsDefault,
  onVerifyStripeACHPaymentMethod,
  isDeleteOptionDisabled,
}: PaymentMethodListItemBaseProps<StripeACHPayment>) => {
  return (
    <Card className={styles.paymentMethodsListItem} elevation={0}>
      <div className={styles.paymentMethodsListItemHeader}>
        <PaymentMethodVendorImage
          paymentMethod={paymentMethod}
          className={styles.paymentMethodsListItemVendorLogo}
        />
        {!paymentMethod.method.verified_at && (
          <Typography
            component="p"
            size="small"
            className={styles.paymentMethodsListItemDetailsItemWarning}
          >
            Payment Method Not Verified
          </Typography>
        )}

        <PaymentMethodItemMenu
          paymentMethod={paymentMethod}
          onDelete={onDelete}
          isDeleteOptionDisabled={isDeleteOptionDisabled}
          onSetAsDefault={onSetAsDefault}
        />
      </div>
      <div className={styles.paymentMethodsListItemDetails}>
        <StripeACHItem paymentMethod={paymentMethod} />
      </div>
      {!paymentMethod.method.verified_at &&
        onVerifyStripeACHPaymentMethod &&
        !hideVerifyButton && (
          <Button
            size="x-small"
            variant="primary"
            color="secondary"
            className={styles.paymentMethodsListItemDefaultButton}
            onClick={() => onVerifyStripeACHPaymentMethod(paymentMethod)}
          >
            Verify
          </Button>
        )}
      {paymentMethod.is_default && <PaymentMethodDetaultTag />}
    </Card>
  );
};

export const PaymentMethodStripeCCListItem = ({
  paymentMethod,
  onDelete,
  onSetAsDefault,
  isDeleteOptionDisabled,
}: PaymentMethodListItemBaseProps<StripeCreditCardPayment>) => {
  return (
    <Card className={styles.paymentMethodsListItem} elevation={0}>
      <div className={styles.paymentMethodsListItemHeader}>
        <PaymentMethodVendorImage
          paymentMethod={paymentMethod}
          className={styles.paymentMethodsListItemVendorLogo}
        />

        <PaymentMethodItemMenu
          paymentMethod={paymentMethod}
          onDelete={onDelete}
          isDeleteOptionDisabled={isDeleteOptionDisabled}
          onSetAsDefault={onSetAsDefault}
        />
      </div>
      <div className={styles.paymentMethodsListItemDetails}>
        <StripeCCItem paymentMethod={paymentMethod} />
      </div>
      {paymentMethod.is_default && <PaymentMethodDetaultTag />}
    </Card>
  );
};

export const PaymentMethodStripeSEPAListItem = ({
  paymentMethod,
  onDelete,
  onSetAsDefault,
  isDeleteOptionDisabled,
}: PaymentMethodListItemBaseProps<StripeSEPAPayment>) => {
  return (
    <Card className={styles.paymentMethodsListItem} elevation={0}>
      <div className={styles.paymentMethodsListItemHeader}>
        <PaymentMethodVendorImage
          paymentMethod={paymentMethod}
          className={styles.paymentMethodsListItemVendorLogo}
        />
        <PaymentMethodItemMenu
          paymentMethod={paymentMethod}
          onDelete={onDelete}
          isDeleteOptionDisabled={isDeleteOptionDisabled}
          onSetAsDefault={onSetAsDefault}
        />
      </div>
      <div className={styles.paymentMethodsListItemDetails}>
        <StripeSEPAItem paymentMethod={paymentMethod} />
      </div>
      {paymentMethod.is_default && <PaymentMethodDetaultTag />}
    </Card>
  );
};
